import { Box, Divider, HStack, Stack, Text } from "@chakra-ui/react";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { LinkIcon } from "modules/icons";
import {
  MessageThreadFragment,
  RelatedMessageThreadsQuery,
  RelatedMessageThreadsQueryVariables,
} from "gql/graphql";
import { useQuery } from "@apollo/client";
import RELATED_MESSAGE_THREADS_QUERY from "../_networking/related-message-threads";
import {
  MESSAGE_ID_QUERY_STRING,
  MESSAGE_THREAD_ID_QUERY_STRING,
} from "modules/hooks/use-query-string";
import { useRouter } from "next/router";
import { useOverlay } from "modules/providers/overlay-provider";
import RelatedMessageThread from "../_shared/related-message-thread";

interface IProps {
  messageThread: MessageThreadFragment;
}

const MessageThreadLinkedItems = ({ messageThread }: IProps) => {
  const { pathname, query, push } = useRouter();
  const { closeDrawer } = useOverlay();
  const { data } = useQuery<
    RelatedMessageThreadsQuery,
    RelatedMessageThreadsQueryVariables
  >(RELATED_MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThread.id,
    },
  });

  const onRelatedMessageThreadClick = useCallback(
    (messageThreadId: string) => () => {
      closeDrawer();
      const newQuery = { ...query };
      delete newQuery[MESSAGE_ID_QUERY_STRING];
      newQuery[MESSAGE_THREAD_ID_QUERY_STRING] = messageThreadId;
      push({
        pathname,
        query: newQuery,
      });
    },
    [closeDrawer, pathname, push, query],
  );

  const _messageThread = useMemo(() => data?.messageThreads[0], [data]);
  const relatedMessageThreads = _messageThread?.relatedMessageThreadList;

  if (!relatedMessageThreads || relatedMessageThreads.length === 0) {
    return null;
  }

  return (
    <Stack w="full">
      <HStack>
        <LinkIcon boxSize={6} fill={colors.blackSecondary} />
        <Text fontFamily="Outfit" fontSize={18} fontWeight={600}>
          {`Linked Items (${relatedMessageThreads.length})`}
        </Text>
      </HStack>
      <Divider borderColor={colors.black} />
      {relatedMessageThreads.map((relatedMessageThread) => (
        <Box
          key={relatedMessageThread.id}
          onClick={onRelatedMessageThreadClick(relatedMessageThread.id)}
          cursor="pointer"
        >
          <RelatedMessageThread relatedMessageThread={relatedMessageThread} />
        </Box>
      ))}
    </Stack>
  );
};

export default memo(MessageThreadLinkedItems);
